import { useAppState } from 'AppStateProvider';
import type { Contact, EnrichedContactWithCrm, RevealBulkResponse } from 'domain/contact';
import type { Crm } from 'domain/crm';
import { useQuota } from 'features/quota/hooks/useQuota';
import { appStore } from 'services/appStore';
import { translate } from 'services/i18n';

import { MatchingState } from './MatchingState';
import { PreviewLoadingState } from './PreviewLoadingState';
import { StyledModal, StyledModalFooter } from './styles';
import { UnrevealedContactsState } from './UnrevealedContactsState';
import { useContactsBulkExport } from './useContactsBulkExport';

export type ContactsExportPreviewModalProps = {
  crm: Crm;
  isOpen: boolean;
  onClose(): void;
  enrichedContactsWithCRM: EnrichedContactWithCrm[];
  setEnrichedContactsWithCRM: (contacts: EnrichedContactWithCrm[]) => void;
  onConfirm(props: { updatedContacts?: EnrichedContactWithCrm[]; revealResult?: RevealBulkResponse }): void;
  onExportAsLeadAfterReveal(revealResponse?: RevealBulkResponse, updatedContacts?: Contact[]): void;
  isExportAsLeadAfterReveal: boolean;
};

export const BulkContactsExportPreviewModal = ({
  crm,
  isOpen,
  enrichedContactsWithCRM,
  setEnrichedContactsWithCRM,
  onConfirm,
  onClose,
  onExportAsLeadAfterReveal,
  isExportAsLeadAfterReveal,
}: ContactsExportPreviewModalProps) => {
  const { selectedContacts } = useAppState();

  const contactsToReveal = selectedContacts.filter((c) => !c.isRevealed).length;
  const hasUnrevealedContacts = contactsToReveal > 0;

  const { getRevealCost } = useQuota();

  const {
    isLoading,
    unmatchedContacts,
    isSubmitDisabled,
    matchedCount,
    total,
    contactsToExportCount,
    onContactChange,
    onContinueToReveal,
    revealResult,
    resetRevealResult,
  } = useContactsBulkExport({
    crm,
    isEnabled: isOpen && !hasUnrevealedContacts,
    enrichedContactsWithCRM,
    setEnrichedContactsWithCRM,
    onConfirm,
    onExportAsLeadAfterReveal,
    isExportAsLeadAfterReveal,
  });

  const renderContent = () => {
    if (isLoading) {
      return <PreviewLoadingState crm={crm} />;
    }

    if (hasUnrevealedContacts) {
      return <UnrevealedContactsState revealCredits={getRevealCost(contactsToReveal)} />;
    }

    if (unmatchedContacts.length > 0) {
      return (
        <MatchingState
          contacts={unmatchedContacts}
          matchedCount={matchedCount}
          unmatchedContacts={unmatchedContacts}
          total={total}
          crm={crm}
          isOpen={isOpen}
          onContactChange={onContactChange}
        />
      );
    }
  };

  const handleClose = () => {
    if (revealResult) {
      appStore.setSuccessToast({
        content: translate('contacts.bulk.export_to_crm.toast.success_reveal_partial', {
          totalCount: total,
          successCount: 0,
          revealedSuccessCount: revealResult.uniqueCount,
          revealedTotalCount: total,
        }),
        showCloseIcon: true,
      });
      resetRevealResult();
    }
    onClose();
  };

  const getHeadingKey = () => {
    if (isLoading) {
      return 'contacts.bulk.export_to_crm.preview_modal.heading.loading';
    }
    if (hasUnrevealedContacts) {
      return 'contacts.bulk.export_to_crm.preview_modal.heading.reveal';
    }
    return 'contacts.bulk.export_to_crm.preview_modal.heading';
  };

  return (
    <StyledModal
      isOpen={isOpen}
      onClose={handleClose}
      heading={translate(getHeadingKey())}
      footerJustifyContent={'flex-end'}
      footerLeftText={
        isLoading || hasUnrevealedContacts ? undefined : (
          <StyledModalFooter>
            {translate('contacts.bulk.export_to_crm.preview_modal.footer')} <span>{contactsToExportCount}</span>
          </StyledModalFooter>
        )
      }
      confirmButtonProps={
        isLoading
          ? undefined
          : {
              children: translate(hasUnrevealedContacts ? 'common.continue' : 'contact_card.export_crm'),
              onClick: hasUnrevealedContacts ? onContinueToReveal : () => onConfirm({ revealResult }),
              disabled: hasUnrevealedContacts ? false : isSubmitDisabled,
              variant: 'primary',
            }
      }
      cancelButtonProps={{
        onClick: handleClose,
        children: translate('common.cancel'),
        variant: 'flat',
      }}
      data-automation="CrmExportPreviewModal"
      contentHorizontalPadding={0}
    >
      {renderContent()}
    </StyledModal>
  );
};
